// Design System Typography Tokens
// Consistent typography scale and styles

// Font families
export const fontFamilies = {
  primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  secondary: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
  display: '"Poppins", "Inter", sans-serif',
};

// Font weights
export const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
};

// Font sizes with consistent scale
export const fontSizes = {
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  base: '1rem',     // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem',    // 48px
  '6xl': '3.75rem', // 60px
  '7xl': '4.5rem',  // 72px
};

// Line heights
export const lineHeights = {
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2,
};

// Letter spacing
export const letterSpacing = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
};

// Typography variants
export const typography = {
  // Display text (large headlines)
  display: {
    large: {
      fontFamily: fontFamilies.display,
      fontSize: fontSizes['6xl'],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights.tight,
      letterSpacing: letterSpacing.tight,
    },
    medium: {
      fontFamily: fontFamilies.display,
      fontSize: fontSizes['5xl'],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights.tight,
      letterSpacing: letterSpacing.tight,
    },
    small: {
      fontFamily: fontFamilies.display,
      fontSize: fontSizes['4xl'],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights.tight,
      letterSpacing: letterSpacing.normal,
    },
  },
  
  // Headings
  heading: {
    h1: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes['3xl'],
      fontWeight: fontWeights.bold,
      lineHeight: lineHeights.tight,
      letterSpacing: letterSpacing.tight,
    },
    h2: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes['2xl'],
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights.tight,
      letterSpacing: letterSpacing.normal,
    },
    h3: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.xl,
      fontWeight: fontWeights.semibold,
      lineHeight: lineHeights.snug,
      letterSpacing: letterSpacing.normal,
    },
    h4: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.lg,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights.snug,
      letterSpacing: letterSpacing.normal,
    },
    h5: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.base,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.normal,
    },
    h6: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.wide,
    },
  },
  
  // Body text
  body: {
    large: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.lg,
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights.relaxed,
      letterSpacing: letterSpacing.normal,
    },
    medium: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.base,
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.normal,
    },
    small: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.normal,
    },
  },
  
  // Labels and captions
  label: {
    large: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.base,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.normal,
    },
    medium: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.normal,
    },
    small: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.xs,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.wide,
    },
  },
  
  // Caption text
  caption: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.regular,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacing.wide,
  },
  
  // Overline text
  overline: {
    fontFamily: fontFamilies.primary,
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.medium,
    lineHeight: lineHeights.normal,
    letterSpacing: letterSpacing.wider,
    textTransform: 'uppercase',
  },
  
  // Code and monospace
  code: {
    inline: {
      fontFamily: fontFamilies.secondary,
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.normal,
    },
    block: {
      fontFamily: fontFamilies.secondary,
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.regular,
      lineHeight: lineHeights.relaxed,
      letterSpacing: letterSpacing.normal,
    },
  },
  
  // Button text
  button: {
    large: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.base,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.wide,
    },
    medium: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.wide,
    },
    small: {
      fontFamily: fontFamilies.primary,
      fontSize: fontSizes.xs,
      fontWeight: fontWeights.medium,
      lineHeight: lineHeights.normal,
      letterSpacing: letterSpacing.wider,
    },
  },
};

// Responsive typography helpers
export const responsiveTypography = {
  // Scale typography based on screen size
  scale: {
    mobile: 0.875,  // 87.5% of base size
    tablet: 1,      // 100% of base size
    desktop: 1,     // 100% of base size
    large: 1.125,   // 112.5% of base size
  },
  
  // Responsive font sizes
  responsive: {
    xs: {
      mobile: fontSizes.xs,
      desktop: fontSizes.xs,
    },
    sm: {
      mobile: fontSizes.xs,
      desktop: fontSizes.sm,
    },
    base: {
      mobile: fontSizes.sm,
      desktop: fontSizes.base,
    },
    lg: {
      mobile: fontSizes.base,
      desktop: fontSizes.lg,
    },
    xl: {
      mobile: fontSizes.lg,
      desktop: fontSizes.xl,
    },
    '2xl': {
      mobile: fontSizes.xl,
      desktop: fontSizes['2xl'],
    },
    '3xl': {
      mobile: fontSizes['2xl'],
      desktop: fontSizes['3xl'],
    },
    '4xl': {
      mobile: fontSizes['3xl'],
      desktop: fontSizes['4xl'],
    },
    '5xl': {
      mobile: fontSizes['4xl'],
      desktop: fontSizes['5xl'],
    },
    '6xl': {
      mobile: fontSizes['5xl'],
      desktop: fontSizes['6xl'],
    },
  },
};

export default {
  families: fontFamilies,
  weights: fontWeights,
  sizes: fontSizes,
  lineHeights,
  letterSpacing,
  variants: typography,
  responsive: responsiveTypography,
};